import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import klubService from '../../services/klubService';
import { notify } from '../../components/common/AppToast';
import { ROUTES } from '../../config/routes';
import { goBackOr } from '../../utils/historyBack';
import {
  AppButton,
  AppCard,
  AppEmptyState,
  AppFormField,
  AppInput,
  AppSpinner,
} from '../../components/ui';

function displayDate(value) {
  if (!value) return '—';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

const EMPTY_FORM = {
  nom: '',
  prenom: '',
  email: '',
  date_entree: '',
  date_sortie: '',
  nb_achats: '',
};

const KlubCeSalariesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [adherent, setAdherent] = useState(null);
  const [ceId, setCeId] = useState(null);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingAchats, setEditingAchats] = useState(null);
  const skipAchatsBlur = useRef(false);

  const loadSalaries = useCallback(async (currentCeId) => {
    const salaries = await klubService.listCeSalaries(currentCeId);
    setRows(salaries);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    klubService.getAdherent(id)
      .then(async (data) => {
        if (cancelled) return;
        setAdherent(data);
        const nextCeId = data?.ce?.id ?? null;
        setCeId(nextCeId);
        if (nextCeId) {
          await loadSalaries(nextCeId);
        } else if (!cancelled) {
          setRows([]);
        }
      })
      .catch((err) => {
        if (!cancelled) notify.error(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, loadSalaries]);

  const ensureCeId = async () => {
    if (ceId) return ceId;
    const updated = await klubService.updateAdherent(id, { ce: { listing_donne: 'en_attente' } });
    const nextCeId = updated?.ce?.id;
    if (!nextCeId) {
      throw new Error('Impossible de créer la fiche CE');
    }
    setCeId(nextCeId);
    setAdherent(updated);
    return nextCeId;
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    const nom = form.nom.trim();
    if (!nom) {
      notify.error('Le nom est requis');
      return;
    }
    setSaving(true);
    try {
      const currentCeId = await ensureCeId();
      await klubService.createCeSalarie(currentCeId, {
        nom,
        prenom: form.prenom.trim(),
        email: form.email.trim(),
        date_entree: form.date_entree || null,
        date_sortie: form.date_sortie || null,
        nb_achats: form.nb_achats === '' ? 0 : Number(form.nb_achats),
        statut: 'actif',
      });
      setForm(EMPTY_FORM);
      await loadSalaries(currentCeId);
      notify.success('Salarié ajouté');
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const patchRow = async (row, patch) => {
    try {
      const updated = await klubService.updateCeSalarie(ceId, row.id, patch);
      setRows((prev) => prev.map((item) => (item.id === row.id ? updated : item)));
    } catch (err) {
      notify.error(err.message);
    }
  };

  const toggleStatut = (row) => {
    patchRow(row, { statut: row.statut === 'resilie' ? 'actif' : 'resilie' });
  };

  const commitAchats = async (row) => {
    const raw = editingAchats?.value ?? '';
    setEditingAchats(null);
    const next = raw === '' ? 0 : Number(raw);
    if (Number.isNaN(next) || next === row.nb_achats) return;
    await patchRow(row, { nb_achats: next });
  };

  return (
    <MasterLayout>
      <Breadcrumb title="Salariés CE" hideHeading />
      <div className="d-flex justify-content-between align-items-center mb-24 flex-wrap gap-3">
        <div>
          <h4 className="fw-semibold mb-1">Salariés CE</h4>
          <p className="text-secondary-light mb-0">{adherent?.entreprise || 'Fiche client'}</p>
        </div>
        <AppButton
          size="sm"
          variant="outline-secondary"
          icon="mdi:arrow-left"
          onClick={() => goBackOr(navigate, ROUTES.klub.adherentDetail(id))}
        >
          Retour à la fiche
        </AppButton>
      </div>

      {loading ? (
        <AppSpinner />
      ) : (
        <>
          <AppCard title="Ajouter un salarié" className="mb-3">
            <form onSubmit={handleCreate}>
              <div className="row g-3 align-items-end">
                <div className="col-12 col-md-4 col-xl-2">
                  <AppFormField label="Nom" htmlFor="salarie-nom" className="mb-0">
                    <AppInput id="salarie-nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} required />
                  </AppFormField>
                </div>
                <div className="col-12 col-md-4 col-xl-2">
                  <AppFormField label="Prénom" htmlFor="salarie-prenom" className="mb-0">
                    <AppInput id="salarie-prenom" value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} />
                  </AppFormField>
                </div>
                <div className="col-12 col-md-4 col-xl-3">
                  <AppFormField label="Mail" htmlFor="salarie-email" className="mb-0">
                    <AppInput id="salarie-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </AppFormField>
                </div>
                <div className="col-6 col-xl-2">
                  <AppFormField label="Date d’entrée" htmlFor="salarie-entree" className="mb-0">
                    <AppInput id="salarie-entree" type="date" value={form.date_entree} onChange={(e) => setForm({ ...form, date_entree: e.target.value })} />
                  </AppFormField>
                </div>
                <div className="col-6 col-xl-2">
                  <AppFormField label="Date de sortie" htmlFor="salarie-sortie" className="mb-0">
                    <AppInput id="salarie-sortie" type="date" value={form.date_sortie} onChange={(e) => setForm({ ...form, date_sortie: e.target.value })} />
                  </AppFormField>
                </div>
                <div className="col-6 col-xl-1">
                  <AppFormField label="Nb achats" htmlFor="salarie-achats" className="mb-0">
                    <AppInput id="salarie-achats" type="number" min="0" value={form.nb_achats} onChange={(e) => setForm({ ...form, nb_achats: e.target.value })} />
                  </AppFormField>
                </div>
                <div className="col-auto">
                  <AppButton type="submit" variant="primary" icon="mdi:plus" loading={saving} loadingLabel="Ajout…">
                    Ajouter
                  </AppButton>
                </div>
              </div>
            </form>
          </AppCard>

          {rows.length === 0 ? (
            <AppEmptyState icon="mdi:account-group-outline" message="Aucun salarié" />
          ) : (
            <div className="card">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Nom</th>
                        <th>Prénom</th>
                        <th>Mail</th>
                        <th>Date d’entrée</th>
                        <th>Date de sortie</th>
                        <th>Nb achats</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const actif = row.statut !== 'resilie';
                        const editing = editingAchats?.id === row.id;
                        return (
                          <tr key={row.id}>
                            <td className="fw-medium">{row.nom}</td>
                            <td>{row.prenom || '—'}</td>
                            <td>{row.email || '—'}</td>
                            <td>{displayDate(row.date_entree)}</td>
                            <td>{displayDate(row.date_sortie)}</td>
                            <td>
                              {editing ? (
                                <AppInput
                                  type="number"
                                  min="0"
                                  size="sm"
                                  value={editingAchats.value}
                                  autoFocus
                                  aria-label={`Nombre d’achats de ${row.prenom || ''} ${row.nom}`}
                                  onChange={(event) => setEditingAchats({ id: row.id, value: event.target.value })}
                                  onBlur={() => {
                                    if (skipAchatsBlur.current) {
                                      skipAchatsBlur.current = false;
                                      return;
                                    }
                                    commitAchats(row);
                                  }}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter') event.currentTarget.blur();
                                    if (event.key === 'Escape') {
                                      skipAchatsBlur.current = true;
                                      setEditingAchats(null);
                                      event.currentTarget.blur();
                                    }
                                  }}
                                />
                              ) : (
                                <AppButton
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => setEditingAchats({ id: row.id, value: row.nb_achats ?? 0 })}
                                >
                                  {row.nb_achats ?? 0}
                                </AppButton>
                              )}
                            </td>
                            <td className="text-end">
                              <AppButton
                                size="sm"
                                variant={actif ? 'success' : 'danger'}
                                onClick={() => toggleStatut(row)}
                              >
                                {actif ? 'Actif' : 'Résilié'}
                              </AppButton>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </MasterLayout>
  );
};

export default KlubCeSalariesPage;
